import {
  CreateJobNoteRequest,
  ListJobNotesRequest,
  ArchiveJobNoteRequest,
  JobNote as JobNoteProto,
} from '@taxfyle/api-internal/internal/job_note_pb'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import orderBy from 'lodash/orderBy'
import { Observable } from 'rxjs'
import { map, share } from 'rxjs/operators'
import { WorkAPI } from '../API'
import { collection } from '../../infra/collection'
import { JobNoteProps, JobNote } from './JobNote'
import { CursorPaged, CursorPageQuery, Id } from '../../types/definitions'
import { timestampToISO } from '../../utils/grpc-util'
import { DateZero } from '../../utils/date-util'
import { filterNonNil } from '../../utils/rx-util'
import { PrimeMemberByPublicId } from 'iam/MemberStore'

/**
 * A realtime event for a job note.
 */
export type JobNoteRealtimeEvent = JobNoteCreatedEvent | JobNoteArchivedEvent

/**
 * A note was created on a job.
 */
export interface JobNoteCreatedEvent {
  type: 'JobNoteCreated'
  jobId: Id
  noteId: Id
}

/**
 * A note was archived on a job.
 */
export interface JobNoteArchivedEvent {
  type: 'JobNoteArchived'
  jobId: Id
  noteId: Id
}

/**
 * Store for job notes.
 */
export class JobNoteStore {
  jobNotes = collection<JobNoteProps, JobNote>({
    getDataId: (attrs) => attrs.id,
    getModelId: (model) => model.id,
    create: () => new JobNote(this.primeMember),
  })

  readonly events$: Observable<JobNoteRealtimeEvent> =
    this.api.jobNotes.events$.pipe(
      map((e) => this._mapJobNoteRealtimeEvent(e)),
      filterNonNil(),
      share(),
    )

  constructor(
    private readonly api: WorkAPI,
    private readonly primeMember: PrimeMemberByPublicId,
  ) {}

  /**
   * Returns loaded notes. Newest first
   */
  notesForJob(jobId: Id) {
    return orderBy(
      this.jobNotes.filter((n) => n.jobId === jobId),
      (n) => n.dateCreated,
      'desc',
    )
  }

  /**
   * Fetches a page of active notes.
   */
  async listForJob(
    jobId: Id,
    paging?: CursorPageQuery,
  ): Promise<CursorPaged<JobNote>> {
    const request = new ListJobNotesRequest()
    request.setJobId(Id.assert(jobId))
    request.setPageSize(paging?.limit ?? 20)
    if (paging?.after) {
      request.setPageToken(paging.after)
    }
    const response = await this.api.jobNotes.listJobNotes(request)

    const mapped = response
      .getJobNotesList()
      .map((note: JobNoteProto) => this._mapToProps(note))

    return {
      data: this.jobNotes.set(mapped),
      cursor: response.getNextPageToken() || null,
    }
  }

  /**
   * Creates a note.
   */
  async createForJob(
    jobId: Id,
    body: string,
    recipientUserPublicIds: Id[] = [],
  ) {
    const request = new CreateJobNoteRequest()
    request.setJobId(Id.assert(jobId))
    request.setBody(body)
    request.setRecipientUserPublicIdsList(recipientUserPublicIds.map(Id.assert))

    await this.api.jobNotes.createJobNote(request)
  }

  /**
   * Archives a note.
   */
  async archive(noteId: Id) {
    const request = new ArchiveJobNoteRequest()
    request.setJobNoteId(Id.assert(noteId))

    await this.api.jobNotes.archiveJobNote(request)

    this.removeNoteById(noteId)
  }

  /**
   * Drops a note from the local collection.
   */
  removeNoteById(noteId: Id) {
    this.jobNotes.remove(Id.assert(noteId))
  }

  /**
   * Maps from proto message to props type.
   */
  private _mapToProps(note: JobNoteProto): JobNoteProps {
    return {
      id: note.getId(),
      job_id: note.getJobId(),
      workspace_id: note.getWorkspaceId(),
      body: note.getBody(),
      created_by_user_public_id: note.getCreatedByUserPublicId(),
      recipient_user_public_ids: note.getRecipientUserPublicIdsList(),
      date_created:
        timestampToISO(note.getCreateTime()) ?? DateZero().toISOString(),
    }
  }

  /**
   * Maps a realtime proto event to a job note domain event.
   */
  private _mapJobNoteRealtimeEvent(
    event: RealtimeEvent,
  ): JobNoteRealtimeEvent | null {
    switch (event.getTypeCase()) {
      case RealtimeEvent.TypeCase.JOB_NOTE_CREATED: {
        const payload = event.getJobNoteCreated()
        const jobId = payload?.getJobId()
        const noteId = payload?.getId()
        if (!jobId || !noteId) {
          return null
        }
        return {
          type: 'JobNoteCreated',
          jobId: Id.assert(jobId),
          noteId: Id.assert(noteId),
        }
      }
      case RealtimeEvent.TypeCase.JOB_NOTE_ARCHIVED: {
        const payload = event.getJobNoteArchived()
        const jobId = payload?.getJobId()
        const noteId = payload?.getId()
        if (!jobId || !noteId) {
          return null
        }
        return {
          type: 'JobNoteArchived',
          jobId: Id.assert(jobId),
          noteId: Id.assert(noteId),
        }
      }
    }

    return null
  }
}
